import { useCallback, useEffect, useState } from "react";
import { AlertCircle, Camera, Clock, ListChecks, MonitorSmartphone } from "lucide-react";

import EasyHTTP from "src/lib/config/easyHttp";
import { examRoleQuery, getExamRole } from "src/lib/auth/examRole";
import { addQuestions } from "src/lib/features/test/testSlice";
import { QuestionData } from "src/lib/endpoints/question/question.type";
import { useAppDispatch } from "src/lib/store/store";
import Exam from "./Exam";
import {
  Card,
  DashboardLink,
  Eyebrow,
  OutlineButton,
  PrimaryButton,
  Shell,
  Title,
} from "./ui";

let easyHttp = new EasyHTTP();

// "error": the request failed or the API said status:false.
// "empty": the API answered, but selected no questions for this talent.
type LoadState = "loading" | "error" | "empty" | "ready";

/**
 * The way into the test: fetch the questions, then either explain what is
 * about to happen or explain why it cannot.
 *
 * The load states are carried over from `pages/Home/LandingPage.tsx` rather
 * than reinvented — they were added because the API can answer with zero
 * questions, which used to leave a spinner running forever.
 */
const Intro = () => {
  const [startTest, setStartTest] = useState(false);
  const [loadState, setLoadState] = useState<LoadState>("loading");
  const [questionCount, setQuestionCount] = useState<number | null>(null);
  const [duration, setDuration] = useState<number | null>(null);
  // The test as loaded, kept so Start can open it with the real time left
  const [loadedTest, setLoadedTest] = useState<QuestionData | null>(null);
  // true when they already pressed Start before (a refresh mid-test)
  const [alreadyStarted, setAlreadyStarted] = useState(false);
  const [starting, setStarting] = useState(false);
  const [startFailed, setStartFailed] = useState(false);
  const dispatch = useAppDispatch();

  const loadQuestions = useCallback(async () => {
    setLoadState("loading");
    try {
      const res = await easyHttp.get(
        `/api/v1/dev/getMyRandomizedQuestion${examRoleQuery()}`
      );
      if (!res?.status) {
        console.log(res);
        setLoadState("error");
        return;
      }

      const questionData: QuestionData | undefined = res.data;
      if (!questionData?.questions?.length || !questionData.testDuration) {
        setLoadState("empty");
        return;
      }

      dispatch(addQuestions(questionData));
      setLoadedTest(questionData);
      setAlreadyStarted(Boolean(questionData.started));
      setQuestionCount(questionData.questions.length);
      setDuration(questionData.testDuration);
      setLoadState("ready");
    } catch (error) {
      console.log(error);
      setLoadState("error");
    }
  }, [dispatch]);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  /* Start (or continue) the test. The server starts the clock the first time
     and answers with the time really left, so the timer on screen matches the
     server's even after a refresh. */
  const beginTest = async () => {
    setStarting(true);
    setStartFailed(false);

    const res = await easyHttp.post(
      `/api/v1/dev/exam/start${examRoleQuery()}`,
      {}
    );
    setStarting(false);

    if (!res?.status || !loadedTest) {
      setStartFailed(true);
      return;
    }

    dispatch(addQuestions({ ...loadedTest, testDuration: res.data.testDuration }));
    setStartTest(true);
  };

  if (loadState === "loading") {
    return (
      <Shell className="flex min-h-screen items-center justify-center">
        <Card className="w-full">
          {/* A shape of the page to come, rather than a spinner on a blank
              field: the wait reads as loading rather than as nothing. */}
          <div className="flex animate-pulse flex-col gap-4">
            <div className="h-3 w-28 rounded bg-brand-navy-100" />
            <div className="h-7 w-2/3 rounded bg-brand-navy-100" />
            <div className="h-4 w-full rounded bg-brand-navy-100" />
            <div className="h-4 w-5/6 rounded bg-brand-navy-100" />
            <div className="mt-2 h-11 w-36 rounded-lg bg-brand-navy-100" />
          </div>
          <p className="mt-6 text-sm text-muted-foreground">
            Preparing your test…
          </p>
        </Card>
      </Shell>
    );
  }

  if (loadState === "error" || loadState === "empty") {
    const isError = loadState === "error";

    return (
      <Shell className="flex min-h-screen items-center justify-center">
        <Card className="w-full">
          <div className="flex flex-col gap-4">
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-brand-orange-100 text-brand-orange">
              <AlertCircle className="h-5 w-5" aria-hidden="true" />
            </span>

            <Title>
              {isError ? "We couldn't load your test" : "Your test isn't ready yet"}
            </Title>

            <p className="text-sm leading-relaxed text-muted-foreground">
              {isError
                ? "Please check your connection and try again. If this keeps happening, open the test again from your Droomwork dashboard."
                : "We couldn't find questions for the skills on your profile. Please contact Droomwork support so we can set up your test."}
            </p>

            <div className="mt-2 flex flex-wrap items-center gap-3">
              {isError && (
                <PrimaryButton onClick={loadQuestions}>Try again</PrimaryButton>
              )}
              <OutlineButton
                onClick={() => {
                  window.location.href =
                    process.env.REACT_APP_TALENT_WEB_URL ?? "/";
                }}
              >
                Go to dashboard
              </OutlineButton>
            </div>
            {!isError && (
              <a
                href={
                  process.env.REACT_APP_TALENT_WEB_URL
                    ? `${process.env.REACT_APP_TALENT_WEB_URL.replace(/\/+$/, "")}/support`
                    : "/support"
                }
                className="text-sm font-semibold text-brand-navy underline underline-offset-4 transition-colors hover:text-brand-orange"
              >
                Contact support
              </a>
            )}
          </div>
        </Card>
      </Shell>
    );
  }

  if (startTest) return <Exam />;

  const facts = [
    {
      icon: ListChecks,
      label: "Questions",
      value: questionCount ? `${questionCount}` : "—",
    },
    {
      icon: Clock,
      label: "Time limit",
      // Rounded up: after a refresh the time left is not a whole number
      value: duration ? `${Math.ceil(duration)} min` : "—",
    },
    { icon: MonitorSmartphone, label: "Attempts", value: "One sitting" },
  ];

  return (
    <Shell className="flex min-h-screen items-center justify-center">
      <div className="w-full">
        <div className="mb-6 flex items-center justify-between gap-4">
          <Eyebrow>Droomwork assessment</Eyebrow>
          <DashboardLink />
        </div>

        <Card className="flex flex-col gap-6">
          <div className="flex flex-col gap-3">
            <Title>
              {getExamRole() === "secondary"
                ? "Ready to take your secondary-role test?"
                : "Ready to take the test?"}
            </Title>
            <p className="text-sm leading-relaxed text-muted-foreground">
              {getExamRole() === "secondary"
                ? "This test assesses the specialties, tools and skills listed under your secondary role."
                : "This test assesses your knowledge and problem-solving across the skills on your profile."}
            </p>
          </div>

          {/* The three numbers worth knowing before starting. The old screen
              held them back until the test was already running. */}
          <div className="grid grid-cols-3 gap-3">
            {facts.map(({ icon: Icon, label, value }) => (
              <div key={label} className="rounded-xl bg-surface p-4">
                <Icon
                  className="h-4 w-4 text-brand-orange"
                  aria-hidden="true"
                />
                <div className="mt-2 font-display text-lg font-extrabold text-brand-navy">
                  {value}
                </div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {label}
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-border p-4">
            <div className="flex items-start gap-3">
              <Camera
                className="mt-0.5 h-4 w-4 shrink-0 text-brand-navy"
                aria-hidden="true"
              />
              <p className="text-sm leading-relaxed text-brand-navy">
                Stay on this page and in front of your camera for the whole
                test. Once you start, the timer keeps running even if you leave
                the page, and when it runs out your test is scored as it is.
                Questions you leave unanswered count as wrong.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between gap-4">
            <p className="text-xs text-muted-foreground">
              {startFailed
                ? "We couldn't start your test. Check your connection and try again."
                : "Good luck."}
            </p>
            <PrimaryButton onClick={beginTest} disabled={starting}>
              {starting
                ? "Starting…"
                : alreadyStarted
                ? "Continue test"
                : "Start test"}
            </PrimaryButton>
          </div>
        </Card>
      </div>
    </Shell>
  );
};

export default Intro;
